import type { ReactNode } from 'react'
import StoreSidebar from './StoreSidebar'
import StaffTabBar from './StaffTabBar'
import AutoPrintMount from './AutoPrintMount'
import CashOnlyOwnerBanner from './CashOnlyOwnerBanner'
import { useLayoutPreference } from '../hooks/useLayoutPreference'
import { useStaffAuth } from '../store/useStaffAuth'
import RequireStoreAccess from './RequireStoreAccess'

/**
 * Shell for authenticated store-owner pages. See `useLayoutPreference` for the
 * standard / experiment-1 / experiment-2 A/B/C system.
 *
 * - `standard` / `experiment-2` — single-column mobile-style layout, but the
 *   centered column widens on larger viewports so PC / tablet don't feel
 *   cramped (430px mobile → 640px tablet → 768px desktop). No sidebar, no
 *   multi-column grids — content stays clean and intuitive like mobile.
 * - `experiment-1` — SaaS-style sidebar + dense multi-column grid, opt-in
 *   for owners who explicitly prefer that density.
 *
 * #995 スタッフモードでは下部固定の [StaffTabBar] を出し、固定バーに隠れないよう
 * 末尾にスペーサーを足す（サイドバーは owner 専用に戻した）。
 *
 * #1910 URL に店 (`:storeId`) を持つ画面は、その店を扱ってよいアカウントにだけ中身を出す
 * (`RequireStoreAccess`)。ここに置くのは、店舗管理の画面がほぼ全部このレイアウトを通るため。
 */
export default function StoreLayout({ children }: { children: ReactNode }) {
  const { preference } = useLayoutPreference()
  const staffSession = useStaffAuth((s) => s.session)

  const staffChrome = staffSession ? (
    <>
      <div className="h-20" aria-hidden />
      <StaffTabBar />
    </>
  ) : null

  if (preference === 'experiment-1') {
    return (
      <div className="lg:flex lg:items-stretch lg:min-h-screen">
        <StoreSidebar />
        <main className="min-w-0 flex-1">
          {/* #1431 現金のみ運用中の常時導線 */}
          <CashOnlyOwnerBanner />
          <RequireStoreAccess>
            {/* 案A: どの店舗ページでも注文伝票を自動印刷（headless 常駐）。
              * #1910 門の内側に置く — 外に置くと、権限の無い店の URL でも設定を読み注文を取りに行く */}
            <AutoPrintMount />
            {children}
          </RequireStoreAccess>
          {staffChrome}
        </main>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-[430px] md:max-w-[640px] lg:max-w-[768px]">
      {/* #1431 現金のみ運用中の常時導線 */}
      <CashOnlyOwnerBanner />
      <RequireStoreAccess>
        {/* 案A: どの店舗ページでも注文伝票を自動印刷（headless 常駐）。
          * #1910 門の内側に置く — 外に置くと、権限の無い店の URL でも設定を読み注文を取りに行く */}
        <AutoPrintMount />
        {children}
      </RequireStoreAccess>
      {staffChrome}
    </div>
  )
}
